import { Instagram, Twitter, PhoneCall, MapPin, Clock } from "lucide-react";
import { BRAND, NAV_LINKS } from "@/constants/content";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const RedditIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#FF4500">
    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
  </svg>
);

export const Footer = () => {
  return (
    <footer
      data-testid="footer-section"
      className="pt-14 pb-8 px-4 sm:px-6"
    >
      <div className="max-w-6xl mx-auto glass-panel rounded-[2.5rem] p-8 sm:p-12">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <a
              href="#home"
              data-testid="footer-logo"
              className="flex items-center gap-2 mb-4"
            >
              <img
                src={BRAND.logoUrl}
                alt="Raj Home Massage logo"
                className="w-10 h-10 object-contain"
              />
              <span className="font-headline font-bold text-lg text-vapor-onbackground">Raj Home Massage</span>
            </a>
            <p className="text-xs text-vapor-onsurfacevariant tracking-wide mb-2">{BRAND.tagline}</p>
            <p className="text-xs text-vapor-onsurfacevariant leading-relaxed">
              <strong>Raj Home Massage</strong><br />
              Andheri West, Mumbai<br />
              +91 9702424106
            </p>
          </div>

          <div>
            <h4 className="font-headline text-sm font-bold text-vapor-secondary uppercase tracking-widest mb-4">
              Quick Links
            </h4>
            <ul className="flex flex-col gap-2.5">
              {NAV_LINKS.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    data-testid={`footer-link-${link.label.toLowerCase()}`}
                    className="text-sm text-vapor-onsurfacevariant hover:text-vapor-secondary transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-headline text-sm font-bold text-vapor-secondary uppercase tracking-widest mb-4">
              Contact
            </h4>
            <ul className="flex flex-col gap-3 text-sm text-vapor-onsurfacevariant">
              <li data-testid="footer-address" className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-vapor-secondary mt-0.5 shrink-0" />
                {BRAND.address}
              </li>
              <li>
                <a
                  href={BRAND.phoneHref}
                  data-testid="footer-phone-link"
                  className="flex items-center gap-2 hover:text-vapor-secondary transition-colors"
                >
                  <PhoneCall className="w-4 h-4 text-vapor-secondary" />
                  {BRAND.phone}
                </a>
              </li>
              <li data-testid="footer-timing" className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-vapor-secondary" />
                {BRAND.timing}
              </li>
              <li className="text-xs italic text-vapor-onsurfacevariant/80">
                {BRAND.couplesNote}
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-headline text-sm font-bold text-vapor-secondary uppercase tracking-widest mb-4">
              Follow Us
            </h4>
            <div className="flex flex-wrap items-center gap-3">
              <a
                href={BRAND.googleBusinessHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-google-link"
                aria-label="Google Business Profile"
                className="w-10 h-10 rounded-full glass-surface flex items-center justify-center hover:scale-110 transition-all"
              >
                <GoogleIcon />
              </a>
              <a
                href={BRAND.instagramHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram-link"
                aria-label="Instagram"
                className="w-10 h-10 rounded-full glass-surface flex items-center justify-center text-vapor-onsurfacevariant hover:text-vapor-secondary transition-all"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href={BRAND.twitterHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-twitter-link"
                aria-label="X (Twitter)"
                className="w-10 h-10 rounded-full glass-surface flex items-center justify-center text-vapor-onsurfacevariant hover:text-vapor-secondary transition-all"
              >
                <Twitter className="w-4 h-4" />
              </a>
              <a
                href={BRAND.redditHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-reddit-link"
                aria-label="Reddit"
                className="w-10 h-10 rounded-full glass-surface flex items-center justify-center hover:scale-110 transition-all"
              >
                <RedditIcon />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-vapor-outlinevariant/40 mt-10 pt-6 text-center">
          <p data-testid="footer-copyright" className="text-xs text-vapor-onsurfacevariant">
            © 2026 Raj Home Massage. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};
